import type { ConnectWithEmailParam, ConnectWithPhoneParam, ConnectWithProvider } from '@particle-network/auth-core';
import {
  ConnectorAlreadyConnectedError,
  type BaseErrorType,
  type Connector,
  type ConnectorAlreadyConnectedErrorType,
  type ErrorType,
} from '@particle-network/connector-core';
import type { Config } from '../createConfig';
import type { SocialAuthType } from '../utils/social';

export type ConnectWithSocialParam = {
  socialType: SocialAuthType;
  prompt?: 'none' | 'consent' | 'select_account';
};

export type AuthConnectParams =
  | Required<ConnectWithPhoneParam>
  | Required<ConnectWithEmailParam>
  | ConnectWithProvider
  | ConnectWithSocialParam;

export type PasskeyParams = {
  isRegistering?: boolean;
};

export type ConnectParameters = {
  connector: Connector;
  chainId?: number;
  authParams?: AuthConnectParams; // only particle auth need this.
  passkeyParams?: PasskeyParams;
};

export type ConnectReturnType = {
  accounts: readonly [string, ...string[]];
  chainId: number;
};

export type ConnectErrorType = ConnectorAlreadyConnectedErrorType | BaseErrorType | ErrorType;

export async function connect(config: Config, parameters: ConnectParameters): Promise<ConnectReturnType> {
  const connector = parameters.connector;

  // Check if connector is already connected
  if (connector.uid === config.state.current) throw new ConnectorAlreadyConnectedError();

  try {
    config.setState((x) => ({ ...x, status: 'connecting' }));
    connector.emitter.emit('message', { type: 'connecting' });

    const data = await connector.connect({
      chainId: parameters.chainId,
      ...parameters.authParams,
      ...parameters.passkeyParams,
    });
    const accounts = data.accounts as readonly [string, ...string[]];

    connector.emitter.off('connect', config._internal.events.connect);
    connector.emitter.on('change', config._internal.events.change);
    connector.emitter.on('disconnect', config._internal.events.disconnect);

    await config.storage?.setItem('recentConnectorId', connector.id);
    config.setState((x) => ({
      ...x,
      connections: new Map(x.connections).set(connector.uid, {
        accounts,
        chainId: data.chainId,
        connector: connector,
      }),
      current: connector.uid,
      status: 'connected',
    }));

    return { accounts, chainId: data.chainId };
  } catch (error) {
    config.setState((x) => ({
      ...x,
      // Keep existing connector connected in case of error
      status: x.current ? 'connected' : 'disconnected',
    }));
    throw error;
  }
}
